import axios from "axios";
import { buildApiUrl, getApiBaseUrl } from "../config/api";
import { isBrowserOnline } from "./network";
import { readCache, writeCache } from "./entityCache";
import { addOutboxEntry } from "./outbox";
import { cacheKeyForQueryKey, queryKeys } from "./queryKeys";

const OFFLINE_SUBMISSION_PREFIX = "offline:";

function apiUrl(path) {
  return buildApiUrl(path.startsWith("/") ? path : `/${path}`);
}

/** Fetch with IndexedDB fallback when offline or request fails. */
const TEACHER_API_TIMEOUT_MS = 30_000;

async function fetchWithCache(queryKey, fetcher) {
  const cacheKey = cacheKeyForQueryKey(queryKey);
  if (isBrowserOnline()) {
    try {
      const data = await fetcher();
      await writeCache(cacheKey, data);
      return data;
    } catch (err) {
      const cached = await readCache(cacheKey);
      if (cached != null) return cached;
      throw err;
    }
  }
  const cached = await readCache(cacheKey);
  if (cached != null) return cached;
  throw new Error("You are offline and no saved copy is available for this class.");
}

export async function fetchClassStudents(classId) {
  return fetchWithCache(queryKeys.classStudents(classId), async () => {
    const res = await axios.get(apiUrl(`/Sclass/Students/${classId}`), {
      timeout: TEACHER_API_TIMEOUT_MS,
    });
    if (Array.isArray(res.data)) return res.data;
    if (res.data?.message === "No students found") return [];
    return [];
  });
}

export async function fetchClassMeta(classId) {
  return fetchWithCache(queryKeys.classMeta(classId), async () => {
    const res = await axios.get(apiUrl(`/Sclass/${classId}`), {
      timeout: TEACHER_API_TIMEOUT_MS,
    });
    if (res.data?.message) return null;
    return res.data;
  });
}

export async function fetchAttendanceSubmissions(teacherId, recordsScope = "current") {
  return fetchWithCache(queryKeys.attendanceSubmissions(teacherId, recordsScope), async () => {
    const res = await axios.get(apiUrl(`/AttendanceSubmissions/teacher/${teacherId}`), {
      params: { recordsScope },
    });
    return Array.isArray(res.data) ? res.data : [];
  });
}

export async function fetchMarksSubmissions(teacherId, recordsScope = "current") {
  return fetchWithCache(queryKeys.marksSubmissions(teacherId, recordsScope), async () => {
    const res = await axios.get(apiUrl(`/MarksSubmissions/teacher/${teacherId}`), {
      params: { recordsScope },
      timeout: TEACHER_API_TIMEOUT_MS,
    });
    return Array.isArray(res.data) ? res.data : [];
  });
}

export async function fetchSchoolNotices(schoolId) {
  return fetchWithCache(queryKeys.schoolNotices(schoolId), async () => {
    const res = await axios.get(`${getApiBaseUrl()}/NoticeList/${schoolId}`);
    if (res.data?.message) return [];
    return Array.isArray(res.data) ? res.data : [];
  });
}

function optimisticSubmission(base, clientId) {
  return {
    ...base,
    id: clientId,
    _id: clientId,
    status: base.status || "Draft",
    _offlinePending: true,
    updatedAt: new Date().toISOString(),
  };
}

async function postAttendance(payload) {
  const res = await axios.post(apiUrl("/AttendanceSubmission"), payload);
  return res.data;
}

async function submitAttendance(submissionId) {
  const res = await axios.put(apiUrl(`/AttendanceSubmission/${submissionId}/submit`));
  return res.data;
}

async function postMarks(payload) {
  const res = await axios.post(apiUrl("/MarksSubmission"), payload);
  return res.data;
}

async function submitMarks(submissionId) {
  const res = await axios.put(apiUrl(`/MarksSubmission/${submissionId}/submit`));
  return res.data;
}

/**
 * Save attendance draft — queues to outbox when offline.
 * @returns {{ data: Object, queued: boolean }}
 */
export async function saveAttendanceDraft(payload) {
  if (isBrowserOnline()) {
    try {
      const data = await postAttendance(payload);
      return { data, queued: false };
    } catch (err) {
      if (!err.response) {
        await addOutboxEntry("attendance_save", payload);
        const clientId = `${OFFLINE_SUBMISSION_PREFIX}${Date.now()}`;
        return {
          data: optimisticSubmission(
            { ...payload, items: payload.items, sessionDate: payload.sessionDate },
            clientId
          ),
          queued: true,
        };
      }
      throw err;
    }
  }
  await addOutboxEntry("attendance_save", payload);
  const clientId = payload.submissionId || `${OFFLINE_SUBMISSION_PREFIX}${Date.now()}`;
  return {
    data: optimisticSubmission(
      { ...payload, items: payload.items, sessionDate: payload.sessionDate },
      clientId
    ),
    queued: true,
  };
}

export async function submitAttendanceFlow(payload, submissionId) {
  if (isBrowserOnline()) {
    try {
      let subId = submissionId;
      if (String(subId || "").startsWith(OFFLINE_SUBMISSION_PREFIX)) {
        const saved = await postAttendance(payload);
        subId = saved?.id || saved?._id;
      } else if (!subId) {
        const saved = await postAttendance(payload);
        subId = saved?.id || saved?._id;
      }
      if (!subId) throw new Error("Could not save attendance before submit");
      const data = await submitAttendance(subId);
      return { data, queued: false };
    } catch (err) {
      if (!err.response) {
        await addOutboxEntry("attendance_submit", { payload, submissionId });
        return {
          data: optimisticSubmission({ ...payload, status: "Submitted" }, submissionId),
          queued: true,
        };
      }
      throw err;
    }
  }
  await addOutboxEntry("attendance_submit", { payload, submissionId });
  return {
    data: optimisticSubmission({ ...payload, status: "Submitted" }, submissionId),
    queued: true,
  };
}

export async function saveMarksDraft(payload) {
  if (isBrowserOnline()) {
    try {
      const data = await postMarks(payload);
      return { data, queued: false };
    } catch (err) {
      if (!err.response) {
        await addOutboxEntry("marks_save", payload);
        const clientId = `${OFFLINE_SUBMISSION_PREFIX}${Date.now()}`;
        return { data: optimisticSubmission(payload, clientId), queued: true };
      }
      throw err;
    }
  }
  await addOutboxEntry("marks_save", payload);
  const clientId = payload.submissionId || `${OFFLINE_SUBMISSION_PREFIX}${Date.now()}`;
  return { data: optimisticSubmission(payload, clientId), queued: true };
}

export async function submitMarksFlow(payload, submissionId) {
  if (isBrowserOnline()) {
    try {
      let subId = submissionId;
      if (String(subId || "").startsWith(OFFLINE_SUBMISSION_PREFIX)) {
        const saved = await postMarks(payload);
        subId = saved?.id || saved?._id;
      } else if (!subId) {
        const saved = await postMarks(payload);
        subId = saved?.id || saved?._id;
      }
      if (!subId) throw new Error("Could not save marks before submit");
      const data = await submitMarks(subId);
      return { data, queued: false };
    } catch (err) {
      if (!err.response) {
        await addOutboxEntry("marks_submit", { payload, submissionId });
        return {
          data: optimisticSubmission({ ...payload, status: "Submitted" }, submissionId),
          queued: true,
        };
      }
      throw err;
    }
  }
  await addOutboxEntry("marks_submit", { payload, submissionId });
  return {
    data: optimisticSubmission({ ...payload, status: "Submitted" }, submissionId),
    queued: true,
  };
}

export { OFFLINE_SUBMISSION_PREFIX };
